/**
 * Smart data processing utilities for hockey statistics
 * Handles position-specific stats, dual-position players, and data aggregation
 */

import logger from './logger';

/**
 * Process and aggregate player statistics by position
 */
export const processPlayerStats = (players, season = null) => {
  return players.map((player) => {
    try {
      const positions = new Set();
      const statsByPosition = {};
      let totalGames = 0;
      let primaryPosition = determinePlayerPosition(player);

      // Process season stats if available
      if (player.seasonStats && Array.isArray(player.seasonStats)) {
        const relevantStats = season
          ? player.seasonStats.filter((stat) => stat.season?._id === season._id)
          : player.seasonStats;

        relevantStats.forEach((stat) => {
          const position = normalizePosition(stat.statsType, stat);
          positions.add(position);

          // Aggregate stats by position
          if (!statsByPosition[position]) {
            statsByPosition[position] = createEmptyStats(position);
          }

          statsByPosition[position] = aggregateStats(statsByPosition[position], stat);
          totalGames += stat.gp || 0;
        });
      }

      // Fallback to current season stats
      if (positions.size === 0 && player.currentSeasonStats) {
        const position = determineStatsPosition(player.currentSeasonStats, player);
        positions.add(position);
        statsByPosition[position] = player.currentSeasonStats;
        totalGames = player.currentSeasonStats.gp || 0;
      }

      // Determine primary position for dual-position players
      if (positions.size > 1) {
        primaryPosition = determinePrimaryPosition(statsByPosition, player);
      }

      return {
        ...player,
        processedStats: {
          positions: Array.from(positions),
          statsByPosition,
          primaryPosition,
          totalGames,
          isDualPosition: positions.size > 1,
          capabilities: getPlayerCapabilities(player),
        },
      };
    } catch (error) {
      logger.error('Error processing player stats', { player: player.name, error });
      return {
        ...player,
        processedStats: {
          positions: [determinePlayerPosition(player)],
          statsByPosition: {},
          primaryPosition: determinePlayerPosition(player),
          totalGames: 0,
          isDualPosition: false,
          capabilities: getPlayerCapabilities(player),
        },
      };
    }
  });
};

/**
 * Determine a player's primary position based on their profile and capabilities
 */
export const determinePlayerPosition = (player) => {
  const position = player.positionGroup?.toLowerCase();
  if (position === 'goalie') return 'goalie';
  if (position === 'defense') return 'defense';
  if (position === 'forward') return 'forward';

  // Legacy fallback
  if (player.canPlayGoalie && !player.canPlaySkater) return 'goalie';

  return 'forward'; // Default
};

/**
 * Normalize position strings to standard format
 */
export const normalizePosition = (statsType, stat) => {
  if (statsType === 'goalie') return 'goalie';
  if (statsType === 'skater') {
    // Try to determine if forward or defense from stats or context
    // For now, default to 'skater' and let other logic handle it
    return 'skater';
  }

  // Handle legacy data
  if (stat.sa > 0 || stat.ga >= 0 || stat.sv > 0) return 'goalie';

  return 'skater';
};

/**
 * Determine position from stats data
 */
export const determineStatsPosition = (stats, player) => {
  // Check if this looks like goalie stats
  if (stats.sa > 0 || stats.ga >= 0 || stats.sv > 0 || stats.svp !== undefined) {
    return 'goalie';
  }

  // Check player's position group
  const position = player.positionGroup?.toLowerCase();
  if (position === 'goalie') return 'goalie';
  if (position === 'defense') return 'defense';
  if (position === 'forward') return 'forward';

  return 'forward'; // Default
};

/**
 * Determine primary position for dual-position players
 */
export const determinePrimaryPosition = (statsByPosition, player) => {
  const positions = Object.keys(statsByPosition);

  if (positions.length === 1) return positions[0];

  // Compare games played in each position
  let maxGames = 0;
  let primary = positions[0];

  positions.forEach((position) => {
    const games = statsByPosition[position]?.gp || 0;
    if (games > maxGames) {
      maxGames = games;
      primary = position;
    }
  });

  // If tied, use player's natural position
  if (maxGames === 0) {
    if (player.positionGroup) return player.positionGroup.toLowerCase();
    if (player.canPlayGoalie && !player.canPlaySkater) return 'goalie';
  }

  return primary;
};

/**
 * Get player capabilities for dual-position tracking
 */
export const getPlayerCapabilities = (player) => {
  const position = player.positionGroup?.toLowerCase();
  return {
    canPlayForward: position === 'forward' || player.canPlaySkater,
    canPlayDefense: position === 'defense' || player.canPlaySkater,
    canPlayGoalie: position === 'goalie' || player.canPlayGoalie,
    naturalPosition: position || 'forward',
  };
};

/**
 * Create empty stats object for a position
 */
export const createEmptyStats = (position) => {
  const baseStats = {
    gp: 0,
    mins: 0,
  };

  if (position === 'goalie') {
    return {
      ...baseStats,
      sa: 0,
      ga: 0,
      sv: 0,
      svp: 0,
      gaa: 0,
    };
  } else {
    return {
      ...baseStats,
      g: 0,
      a: 0,
      pts: 0,
      pim: 0,
      ppg: 0,
      ppa: 0,
      shg: 0,
      otg: 0,
      eng: 0,
    };
  }
};

/**
 * Aggregate stats from multiple entries
 */
export const aggregateStats = (existing, newStats) => {
  const aggregated = { ...existing };

  // Add countable stats
  const countableStats = [
    'gp',
    'g',
    'a',
    'pts',
    'pim',
    'ppg',
    'ppa',
    'shg',
    'otg',
    'eng',
    'sa',
    'ga',
    'sv',
    'mins',
  ];

  countableStats.forEach((stat) => {
    if (newStats[stat] !== undefined) {
      aggregated[stat] = (aggregated[stat] || 0) + (newStats[stat] || 0);
    }
  });

  // Calculate derived stats
  if (aggregated.sa > 0) {
    aggregated.svp = parseFloat(((aggregated.sv / aggregated.sa) * 100).toFixed(1));
  }

  if (aggregated.mins > 0 && aggregated.ga !== undefined) {
    aggregated.gaa = parseFloat(((aggregated.ga * 60) / aggregated.mins).toFixed(2));
  }

  if (aggregated.g !== undefined && aggregated.a !== undefined) {
    aggregated.pts = aggregated.g + aggregated.a;
  }

  return aggregated;
};

/**
 * Filter players by position for display
 */
export const filterPlayersByPosition = (processedPlayers, position) => {
  switch (position) {
    case 'forwards':
      return processedPlayers.filter(
        (p) =>
          (p.processedStats.primaryPosition === 'forward' ||
            p.processedStats.primaryPosition === 'skater') &&
          p.positionGroup?.toLowerCase() !== 'defense' &&
          p.positionGroup?.toLowerCase() !== 'goalie'
      );

    case 'defense':
      return processedPlayers.filter(
        (p) =>
          p.processedStats.primaryPosition === 'defense' ||
          p.positionGroup?.toLowerCase() === 'defense'
      );

    case 'goalies':
      return processedPlayers.filter(
        (p) =>
          p.processedStats.primaryPosition === 'goalie' ||
          p.processedStats.positions.includes('goalie')
      );

    case 'dual-position':
      return processedPlayers.filter((p) => p.processedStats.isDualPosition);

    default:
      return processedPlayers;
  }
};

/**
 * Sort players by relevant stats
 */
export const sortPlayersByStats = (players, sortBy = 'name', position = 'all') => {
  return [...players].sort((a, b) => {
    switch (sortBy) {
      case 'points': {
        const aPoints = getPlayerMainStats(a, position)?.pts || 0;
        const bPoints = getPlayerMainStats(b, position)?.pts || 0;
        return bPoints - aPoints;
      }

      case 'goals': {
        const aGoals = getPlayerMainStats(a, position)?.g || 0;
        const bGoals = getPlayerMainStats(b, position)?.g || 0;
        return bGoals - aGoals;
      }

      case 'saves': {
        const aSaves = getPlayerMainStats(a, position)?.sv || 0;
        const bSaves = getPlayerMainStats(b, position)?.sv || 0;
        return bSaves - aSaves;
      }

      case 'games': {
        const aGames = a.processedStats?.totalGames || 0;
        const bGames = b.processedStats?.totalGames || 0;
        return bGames - aGames;
      }

      default:
        return (a.name || '').localeCompare(b.name || '');
    }
  });
};

/**
 * Get main stats for a player based on position filter
 */
export const getPlayerMainStats = (player, positionFilter = 'all') => {
  if (!player.processedStats) return null;

  const { statsByPosition, primaryPosition } = player.processedStats;

  if (positionFilter === 'goalies' && statsByPosition.goalie) {
    return statsByPosition.goalie;
  }

  if (positionFilter === 'all') {
    return statsByPosition[primaryPosition] || null;
  }

  return statsByPosition.skater || statsByPosition[primaryPosition] || null;
};

/**
 * Validate stats data integrity
 */
export const validateStatsData = (players) => {
  const issues = [];

  players.forEach((player) => {
    if (!player.name) {
      issues.push(`Player missing name: ${player._id}`);
    }

    if (player.processedStats?.isDualPosition && player.processedStats.positions.length < 2) {
      issues.push(
        `Player ${player.name} marked as dual-position but only has ${player.processedStats.positions.length} position(s)`
      );
    }

    // Check for impossible stats
    Object.entries(player.processedStats?.statsByPosition || {}).forEach(([position, stats]) => {
      if (stats.svp > 100) {
        issues.push(`Player ${player.name} has impossible save percentage: ${stats.svp}%`);
      }

      if (stats.gaa < 0) {
        issues.push(`Player ${player.name} has negative GAA: ${stats.gaa}`);
      }
    });
  });

  if (issues.length > 0) {
    logger.warn('Stats validation issues found', { issues });
  }

  return issues;
};
